// ============================================================================
// Componente: FinanceiroNumerarioProsegur.js
// Objetivo: Importar o relatório semanal da Prosegur (PDF ou Excel) — a perna "Apurado"
//           do Numerário, o dinheiro que de fato chegou ao banco.
// Padrão visual: Financeiro / teal (#0d7a74)
//
// O fluxo é deliberadamente em DOIS passos: escolher o arquivo mostra a PRÉVIA
// (o que foi lido, o que confere), e só um segundo clique grava. Importar
// dinheiro no escuro é como se perde a confiança no número.
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    UploadCloud, FileText, CheckCircle2, AlertTriangle, Loader2, Save, X, History,
} from 'lucide-react';

import {
    COR, COR_CLARA, COR_BORDA, dinheiro, dataBR, corDaDiferenca, th, td, tdNum,
} from './numerarioFormato';

// Lojas que não aparecem no relatório de quarta-feira (HUNGER e P&G).
// Não é erro de leitura — e a tela precisa dizer isso, senão alguém lê ausência
// como "depositou zero". Espelha `server/financeiro/prosegur-lojas.js`, que é a
// fonte da verdade; aqui é só texto de tela.
//
// ⚠️ Estas lojas têm relatório PRÓPRIO da Prosegur (cliente à parte das demais),
//    então a ausência numa semana significa "o relatório desta loja ainda não
//    foi importado" — nunca "não depositou":
//      MAL e MOX  → coleta em outro dia (MAL na sexta, MOX na TERÇA — e não
//                   quarta; o dia do MOX veio do arquivo real, corrigindo o
//                   "segunda" que era suposição). Entraram em 2026-08-17.
//      PCI e PCS  → ATÉ 2026-10-13 não tinham coleta (numerário ia ao
//                   escritório). Isso MUDOU: a Prosegur passou a recolhê-las
//                   (clientes 36326 e 36323), com relatório próprio como o de
//                   MAL/MOX — por isso saíram de "sem coleta" e entraram aqui.
// ⚠️ SEM_COLETA está vazia hoje: no momento toda loja do numerário tem coleta.
//    A lista fica porque, se um dia entrar uma loja sem coleta, o texto já existe.
const AGUARDANDO_RELATORIO = ['MAL', 'MOX', 'PCI', 'PCS'];
const SEM_COLETA = [];

const FinanceiroNumerarioProsegur = () => {
    const inputRef = useRef(null);

    const [arquivo, setArquivo] = useState(null);
    const [previa, setPrevia] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [gravando, setGravando] = useState(false);
    const [mensagem, setMensagem] = useState(null);
    const [guias, setGuias] = useState([]);

    const token = () => localStorage.getItem('token') || localStorage.getItem('authToken');

    const carregarHistorico = useCallback(async () => {
        try {
            const res = await fetch('/api/numerario/prosegur/guias', {
                headers: { Authorization: `Bearer ${token()}` },
            });
            if (!res.ok) return;
            setGuias(await res.json());
        } catch {
            /* histórico é acessório — falhar aqui não pode derrubar a tela */
        }
    }, []);

    useEffect(() => { carregarHistorico(); }, [carregarHistorico]);

    // Envia o arquivo. Sem `confirmar`, o backend só devolve a prévia.
    const enviar = async (confirmar) => {
        if (!arquivo) return;
        const form = new FormData();
        form.append('arquivo', arquivo);
        if (confirmar) form.append('confirmar', 'true');

        confirmar ? setGravando(true) : setCarregando(true);
        setMensagem(null);
        try {
            const res = await fetch('/api/numerario/prosegur/importar', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token()}` },
                body: form,
            });
            const dados = await res.json();
            if (!res.ok) {
                setMensagem({ tipo: 'erro', texto: dados.erro || 'Falha ao ler o relatório.', avisos: dados.avisos });
                if (!confirmar) setPrevia(null);
                return;
            }
            setPrevia(dados);
            if (confirmar) {
                setMensagem({
                    tipo: 'ok',
                    texto: `Relatório de ${dataBR(dados.dtRelatorio)} importado: `
                        + `${dados.inseridas} guia(s) nova(s), ${dados.atualizadas} atualizada(s)`
                        + `${dados.ignoradas ? `, ${dados.ignoradas} ignorada(s) por não conferir` : ''}.`,
                    avisos: dados.avisos,
                });
                setArquivo(null);
                if (inputRef.current) inputRef.current.value = '';
                carregarHistorico();
            }
        } catch (e) {
            setMensagem({ tipo: 'erro', texto: 'Não foi possível falar com o servidor.' });
        } finally {
            setCarregando(false);
            setGravando(false);
        }
    };

    const aoEscolher = (e) => {
        const f = e.target.files?.[0] || null;
        setArquivo(f);
        setPrevia(null);
        setMensagem(null);
    };

    const limpar = () => {
        setArquivo(null);
        setPrevia(null);
        setMensagem(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    // Só oferece gravar se houver ao menos uma guia que passou na conferência.
    const guiasOk = previa?.guias?.filter((g) => g.confere) || [];
    const guiasRuins = previa?.guias?.filter((g) => !g.confere) || [];
    const podeGravar = previa?.previa && guiasOk.length > 0;

    return (
        <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
            <header style={{ marginBottom: 20 }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, color: COR, margin: 0 }}>
                    <FileText size={22} /> Numerário — relatório da Prosegur
                </h2>
                <p style={{ color: '#475569', marginTop: 6, fontSize: 14 }}>
                    O relatório chega toda quinta-feira, referente ao depósito da quarta.
                    Ele traz o <strong>valor apurado</strong> — o dinheiro que a Prosegur contou e levou ao banco.
                    Aceita <strong>PDF ou Excel</strong>: desde 05/08/2026 a Prosegur disponibiliza os dois, e
                    o conteúdo é o mesmo.
                </p>
            </header>

            {/* ── Upload ─────────────────────────────────────────────────── */}
            <section style={{
                background: COR_CLARA, border: `1px solid ${COR_BORDA}`,
                borderRadius: 12, padding: 20, marginBottom: 20,
            }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="application/pdf,.pdf,.xls,.xlsx"
                        onChange={aoEscolher}
                        style={{ display: 'none' }}
                        id="arquivo-prosegur"
                    />
                    <label
                        htmlFor="arquivo-prosegur"
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                            background: '#fff', border: `1px solid ${COR_BORDA}`, color: COR,
                            padding: '10px 16px', borderRadius: 8, fontWeight: 600,
                        }}
                    >
                        <UploadCloud size={18} /> Escolher arquivo
                    </label>

                    <span style={{ color: arquivo ? '#0f172a' : '#94a3b8', fontSize: 14 }}>
                        {arquivo ? arquivo.name : 'Nenhum arquivo selecionado'}
                    </span>

                    {arquivo && !previa && (
                        <button
                            type="button"
                            onClick={() => enviar(false)}
                            disabled={carregando}
                            style={botao(COR, carregando)}
                        >
                            {carregando ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                            {carregando ? 'Lendo…' : 'Ler relatório'}
                        </button>
                    )}

                    {podeGravar && (
                        <button
                            type="button"
                            onClick={() => enviar(true)}
                            disabled={gravando}
                            style={botao('#15803d', gravando)}
                        >
                            {gravando ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                            {gravando ? 'Gravando…' : `Importar ${guiasOk.length} guia(s)`}
                        </button>
                    )}

                    {(arquivo || previa) && (
                        <button type="button" onClick={limpar} style={botao('#64748b', false)}>
                            <X size={16} /> Limpar
                        </button>
                    )}
                </div>
            </section>

            {/* ── Mensagem ───────────────────────────────────────────────── */}
            {mensagem && (
                <div style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start',
                    background: mensagem.tipo === 'ok' ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${mensagem.tipo === 'ok' ? '#bbf7d0' : '#fecaca'}`,
                    color: mensagem.tipo === 'ok' ? '#166534' : '#991b1b',
                    borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 14,
                }}>
                    {mensagem.tipo === 'ok' ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
                    <div>
                        <div>{mensagem.texto}</div>
                        {mensagem.avisos?.map((a, i) => (
                            <div key={i} style={{ marginTop: 4, fontSize: 13, opacity: 0.9 }}>• {a}</div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Prévia ─────────────────────────────────────────────────── */}
            {previa?.guias?.length > 0 && (
                <section style={{ marginBottom: 28 }}>
                    <h3 style={{ fontSize: 16, color: '#0f172a', marginBottom: 4 }}>
                        {previa.previa ? 'Prévia — confira antes de importar' : 'Importado'}
                    </h3>
                    <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 12 }}>
                        {previa.empresa} · relatório de <strong>{dataBR(previa.dtRelatorio)}</strong>
                    </p>

                    <Tabela
                        colunas={['Loja', 'Guia', 'Lacre', 'Envelopes', 'Declarado', 'Apurado', 'Diferença', '']}
                        alinhaDireita={[3, 4, 5, 6]}
                    >
                        {previa.guias.map((g) => (
                            <tr key={`${g.nrGuia}-${g.loja}`} style={{ background: g.confere ? '#fff' : '#fff7ed' }}>
                                <td style={td}><strong>{g.loja}</strong></td>
                                <td style={td}>{g.nrGuia}</td>
                                <td style={td}>{g.nrLacre || '—'}</td>
                                <td style={tdNum}>{g.qtdEnvelopes}</td>
                                <td style={tdNum}>{dinheiro(g.vlDeclarado)}</td>
                                <td style={tdNum}>{dinheiro(g.vlApurado)}</td>
                                <td style={{ ...tdNum, color: corDaDiferenca(g.vlDiferenca) }}>
                                    {dinheiro(g.vlDiferenca)}
                                </td>
                                <td style={td}>
                                    {g.confere
                                        ? <CheckCircle2 size={16} color="#15803d" />
                                        : <span title="A leitura não bateu com os totais do próprio relatório — esta guia não será importada.">
                                            <AlertTriangle size={16} color="#c2410c" />
                                        </span>}
                                </td>
                            </tr>
                        ))}
                    </Tabela>

                    {guiasRuins.length > 0 && (
                        <p style={{ color: '#c2410c', fontSize: 13, marginTop: 10 }}>
                            {guiasRuins.length} guia(s) não conferem com os totais do próprio relatório e
                            <strong> não serão importadas</strong>. Isso costuma significar que o layout do
                            relatório mudou.
                        </p>
                    )}

                    <div style={{ color: '#64748b', fontSize: 13, marginTop: 10, lineHeight: 1.6 }}>
                        <div>
                            <strong>{AGUARDANDO_RELATORIO.join(', ')}</strong> vêm em <strong>relatórios
                            próprios</strong> (cada uma é um cliente à parte na Prosegur; MAL coleta na
                            sexta, MOX na terça) — peça-os à Prosegur para a data certa e importe aqui:
                            eles entram automaticamente na mesma semana das demais lojas.
                        </div>
                        {SEM_COLETA.length > 0 && (
                            <div>
                                <strong>{SEM_COLETA.join(' e ')}</strong> não têm coleta: o numerário é
                                entregue no escritório pela própria loja. Para elas não há relatório a importar.
                            </div>
                        )}
                        <div style={{ marginTop: 4, fontStyle: 'italic' }}>
                            A ausência de uma loja nunca significa que ela deixou de depositar.
                        </div>
                    </div>
                </section>
            )}

            {/* ── Histórico ──────────────────────────────────────────────── */}
            <section>
                <h3 style={{ fontSize: 16, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <History size={18} /> Relatórios já importados
                </h3>
                {guias.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontSize: 14 }}>Nenhum relatório importado ainda.</p>
                ) : (
                    <Tabela
                        colunas={['Data', 'Loja', 'Guia', 'Envelopes', 'Declarado', 'Apurado', 'Diferença', 'Importado por']}
                        alinhaDireita={[3, 4, 5, 6]}
                    >
                        {guias.map((g) => {
                            const dif = Number(g.VL_APURADO) - Number(g.VL_DECLARADO);
                            return (
                                <tr key={g.ID}>
                                    <td style={td}>{dataBR(g.DT_RELATORIO)}</td>
                                    <td style={td}><strong>{g.LOJA}</strong></td>
                                    <td style={td}>{g.NR_GUIA}</td>
                                    <td style={tdNum}>{g.QTD_ENVELOPES}</td>
                                    <td style={tdNum}>{dinheiro(g.VL_DECLARADO)}</td>
                                    <td style={tdNum}>{dinheiro(g.VL_APURADO)}</td>
                                    <td style={{ ...tdNum, color: corDaDiferenca(dif) }}>{dinheiro(dif)}</td>
                                    <td style={td}>{g.IMPORTADO_POR_NOME || '—'}</td>
                                </tr>
                            );
                        })}
                    </Tabela>
                )}
            </section>
        </div>
    );
};

// ── Peças visuais pequenas ──────────────────────────────────────────────────

const botao = (cor, ocupado) => ({
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: cor, color: '#fff', border: 'none', borderRadius: 8,
    padding: '10px 16px', fontWeight: 600, fontSize: 14,
    cursor: ocupado ? 'wait' : 'pointer', opacity: ocupado ? 0.7 : 1,
});

const Tabela = ({ colunas, alinhaDireita = [], children }) => (
    <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
                <tr>
                    {colunas.map((c, i) => (
                        <th key={i} style={{ ...th, textAlign: alinhaDireita.includes(i) ? 'right' : 'left' }}>{c}</th>
                    ))}
                </tr>
            </thead>
            <tbody>{children}</tbody>
        </table>
    </div>
);

export default FinanceiroNumerarioProsegur;
